import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	Action,
	NgxsAfterBootstrap,
	NgxsOnInit,
	Select,
	Selector,
	State,
	StateContext,
	Store,
} from '@ngxs/store';
import { BusinessState } from '@state/business';
import { combineLatest, fromEvent, merge, Observable, timer } from 'rxjs';
import { auditTime, debounceTime, filter, map } from 'rxjs/operators';
import * as authActions from '../auth/auth.actions';
import {
	IdleTimeoutWarningComponent,
	IdleTimeoutWarningDialogData,
} from './components/idle-timeout-warning/idle-timeout-warning.component';
import * as actions from './idle-timeout.actions';
import { IdleTimeoutStateModel } from './idle-timeout.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthState } from '@state/auth';

export const idleTimeoutStateDefaults: IdleTimeoutStateModel = {
	dialogId: null,
	lastActiveAt: null,
};

export const lastActiveAtStorageKey = 'idle-timeout-last-active-at';

/**
 * the number of milliseconds used to debounce user input events;
 * this prevents flooding the subscription
 */
export const userInputDebounceMs = 5000;

/**
 * the interval in milliseconds to check whether conditions are met for the user to be logged out forcibly
 */
export const pollingIntervalMs = 1000;

/**
 * the number, in seconds, that the warning dialog will
 * begin the countdown before autologout is triggered
 */
export const warningDialogDurationSeconds = 59;

export const warningDialogDurationMs = warningDialogDurationSeconds * 1000;

/**
 * a whitelist of `window` events to listen to in order
 * to trigger the wakeup
 * note: scroll appears to cause issues, as it seems to
 * call it in rapid fire DURING scrolling, which floods
 * disabling this for now.
 */
export const windowIdleWakeupEvents: string[] = [
	'click',
	'keydown',
	'keyup',
	'mousemove',
	// 'scroll', // see above comment
	'touchend',
	'touchmove',
	'touchstart',
];

type Ctx = StateContext<IdleTimeoutStateModel>;

@UntilDestroy()
@State<IdleTimeoutStateModel>({
	name: 'idleTimeout',
	defaults: structuredClone(idleTimeoutStateDefaults),
})
@Injectable()
export class IdleTimeoutState implements NgxsOnInit, NgxsAfterBootstrap {
	private userActivityDetector$: Observable<Event>;
	private lastUserActivityTimestamp$: Observable<number>;
	private localStorageLastTouchedTimestamps$: Observable<number>;

	@Select(BusinessState.getIdleTimeoutMs)
	private idleTimeoutMs$: Observable<number>;
	@Select(BusinessState.isIdleTimeoutDisabled)
	private isIdleTimeoutDisabled$: Observable<boolean>;
	@Select(AuthState.isUserLoggedIn)
	private isUserLoggedIn$: Observable<boolean>;

	@Selector([IdleTimeoutState])
	static lastActiveAt(state: IdleTimeoutStateModel): number {
		return state?.lastActiveAt;
	}

	constructor(
		private dialog: MatDialog,
		private store: Store,
	) {}

	ngxsOnInit(ctx: Ctx): void {
		// When idle timeout is disabled (i.e. isIdleTimeoutDisabled$ emits true), then the user activity event is prevented from emitting
		this.userActivityDetector$ = combineLatest([
			merge(
				...windowIdleWakeupEvents.map((eventName) =>
					fromEvent(window, eventName),
				),
			),
			this.isIdleTimeoutDisabled$,
		]).pipe(
			debounceTime(userInputDebounceMs),

			filter(
				([userActivtyEvent, isIdleTimeoutDisabled]) => !isIdleTimeoutDisabled,
			),
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			map(([userActivtyEvent, isIdleTimeoutDisabled]) => userActivtyEvent),
		);

		this.lastUserActivityTimestamp$ = this.userActivityDetector$.pipe(
			map(() => new Date().getTime()),
		);

		this.localStorageLastTouchedTimestamps$ = fromEvent<StorageEvent>(
			window,
			'storage',
		).pipe(
			filter((ev) => ev.key === lastActiveAtStorageKey),
			map((ev) => {
				const { newValue } = ev;

				return newValue === 'null' ? null : Number(newValue);
			}),
		);

		//When user activity is detected, update the timestamp in state and local storage
		this.lastUserActivityTimestamp$
			.pipe(untilDestroyed(this))
			.subscribe((activityTimestamp) => {
				ctx.dispatch([
					new actions.UpdateStoreActivityTimestamp(activityTimestamp),
					new actions.UpdateLocalStorageActivityTimestamp(activityTimestamp),
				]);
			});

		// Update state with latest timestamp from local storage
		this.localStorageLastTouchedTimestamps$
			.pipe(untilDestroyed(this))
			.subscribe((lastActiveAt) => {
				ctx.dispatch(
					new actions.UpdateStoreActivityTimestamp(lastActiveAt, true),
				);
			});

		combineLatest([
			this.idleTimeoutMs$,
			this.isIdleTimeoutDisabled$,
			this.isUserLoggedIn$,
			this.store.select(IdleTimeoutState.lastActiveAt),
			timer(0, pollingIntervalMs),
		])
			.pipe(untilDestroyed(this), auditTime(pollingIntervalMs))

			.subscribe(
				([
					idleTimeoutMs,
					isIdleTimeoutDisabled,
					isUserLoggedIn,
					lastActiveAt,
					timerEvent,
				]) => {
					if (!isUserLoggedIn || isIdleTimeoutDisabled || !lastActiveAt) return;

					const idleTimeoutMaxLimit = lastActiveAt + idleTimeoutMs;
					const currentTime = new Date().getTime();

					// forced logout once max time limit has been reached and close any opened warning dialogs
					if (currentTime >= idleTimeoutMaxLimit) {
						ctx.dispatch([
							new actions.CloseWarningDialog(),
							new actions.Logout(),
						]);
						return;
					}

					const warningDialogStartTime =
						idleTimeoutMaxLimit - warningDialogDurationMs;
					const dialogId = ctx.getState().dialogId;

					// show warning dialog with the time left to register user activity if the user is within the warning period and no dialog has been opened
					if (currentTime >= warningDialogStartTime && !dialogId) {
						const timeRemainingBeforeTimeoutSeconds = Math.floor(
							(idleTimeoutMaxLimit - currentTime) / 1000,
						);
						ctx.dispatch(
							new actions.OpenWarningDialog(timeRemainingBeforeTimeoutSeconds),
						);
						return;
					}

					// if the user is outside the timeout warning period but the warning dialog is open, then close it. This would happen if the timeout period was reset from another tab.
					if (currentTime < warningDialogStartTime && dialogId) {
						ctx.dispatch(new actions.CloseWarningDialog());
						return;
					}
				},
			);
	}

	ngxsAfterBootstrap(ctx: Ctx): void {
		// Set activity timestamps when user logs in to initialize starting state
		this.isUserLoggedIn$
			.pipe(
				untilDestroyed(this),
				filter((isUserLoggedIn) => isUserLoggedIn),
			)
			.subscribe(() => {
				const now = new Date().getTime();

				ctx.dispatch([
					new actions.UpdateStoreActivityTimestamp(now, true),
					new actions.UpdateLocalStorageActivityTimestamp(now, true),
				]);
			});

		// run clean up tasks if the user has been logged out
		this.isUserLoggedIn$
			.pipe(
				untilDestroyed(this),
				filter((isUserLoggedIn) => !isUserLoggedIn),
			)
			.subscribe(() => {
				const dialogId = ctx.getState().dialogId;

				if (dialogId) {
					ctx.dispatch(new actions.CloseWarningDialog());
				}

				ctx.dispatch([
					new actions.UpdateStoreActivityTimestamp(null, true),
					new actions.UpdateLocalStorageActivityTimestamp(null, true),
				]);
			});
	}

	@Action(actions.UpdateLocalStorageActivityTimestamp)
	updateLocalStorageActivityTimestamp(
		ctx: Ctx,
		payload: actions.UpdateLocalStorageActivityTimestamp,
	): void {
		const { dialogId } = ctx.getState();

		// Do not update timestamp if warning dialog is already opened
		if (dialogId && !payload.ignoreOpenedWarningDialog) return;

		localStorage.setItem(lastActiveAtStorageKey, `${payload.lastActiveAt}`);
	}

	@Action(actions.UpdateStoreActivityTimestamp)
	updateStoreActivityTimestamp(
		ctx: Ctx,
		payload: actions.UpdateStoreActivityTimestamp,
	): void {
		const { dialogId } = ctx.getState();

		// Do not update timestamp if warning dialog is already opened
		if (dialogId && !payload.ignoreOpenedWarningDialog) return;

		ctx.patchState({
			lastActiveAt: payload.lastActiveAt,
		});
	}

	@Action(actions.OpenWarningDialog)
	openWarningDialog(
		{ patchState, getState }: Ctx,
		{ countdownSeconds }: actions.OpenWarningDialog,
	): void {
		const { dialogId } = getState();

		if (dialogId) return;

		const data: IdleTimeoutWarningDialogData = { countdownSeconds };
		patchState({
			dialogId: this.dialog.open(IdleTimeoutWarningComponent, {
				data,
				backdropClass: 'blurry',
				closeOnNavigation: false,
				disableClose: true,
			}).id,
		});
	}

	@Action(actions.ResetIdleTimeoutFromWarningDialog)
	resetIdleTimeoutFromWarningDialog(ctx: Ctx): void {
		const now = new Date().getTime();

		ctx.dispatch([
			new actions.CloseWarningDialog(),
			new actions.UpdateStoreActivityTimestamp(now, true),
			new actions.UpdateLocalStorageActivityTimestamp(now, true),
		]);
	}

	@Action(actions.Logout)
	logout(ctx: Ctx, { doNotNotify }: actions.Logout): void {
		ctx.dispatch(new authActions.Logout());

		if (!doNotNotify) {
			ctx.dispatch(
				new authActions.DisplayMessage({
					text: 'You have been automatically logged out due to inactivity',
					type: 'error',
				}),
			);
		}
	}

	@Action(actions.CloseWarningDialog)
	closeWarningDialog({ getState, patchState }: Ctx): void {
		const dialogId = getState()?.dialogId;
		if (dialogId) {
			this.dialog.getDialogById(dialogId)?.close();
			patchState({ dialogId: null });
		}
	}
}
